import React from 'react';
import { Box, Button, Icon, Tooltip, useColorModeValue } from '@chakra-ui/react';

/**
 * Custom map control buttons with Explorer's Journal styling
 */
const MapControl = ({ 
  icon, 
  label, 
  onClick, 
  isActive,
  borderColor = "#9C826B", // Default border color
  tooltipText
}) => {
  const bg = useColorModeValue(
    isActive ? '#C19434' : '#E6DAC0',
    isActive ? '#D6AD60' : '#38291E'
  );
  
  const color = useColorModeValue(
    isActive ? 'white' : '#3B2E1E',
    isActive ? '#2C231B' : '#E6D7B8'
  );
  
  const hoverBg = useColorModeValue(
    isActive ? '#D6AD60' : '#D9CAA7',
    isActive ? '#E6C068' : '#483123'
  );
  
  const button = (
    <Button
      leftIcon={<Box as="span" fontSize="16px">{icon.replace(/&#(\d+);?/g, (match, dec) => String.fromCharCode(dec))}</Box>}
      onClick={onClick}
      bg={bg}
      color={color}
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth="3px"
      borderRadius="md"
      fontFamily="heading"
      fontSize="sm"
      fontWeight={isActive ? "600" : "500"}
      mb={2}
      width="140px"
      height="38px"
      justifyContent="flex-start"
      position="relative"
      transform={isActive ? "none" : undefined}
      transition="all 0.2s ease-out"
      sx={{
        "&::before": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
          bgRepeat: "repeat",
          bgSize: "200px",
          opacity: 0.15,
          pointerEvents: "none",
          zIndex: 0,
          borderRadius: "inherit",
        }
      }}
      _hover={{
        bg: hoverBg,
        transform: isActive ? "none" : "translateY(-1px)",
        boxShadow: isActive ? "none" : "sm",
      }}
      boxShadow={isActive ? "inset 0 1px 3px rgba(0,0,0,0.2)" : "sm"}
    >
      {label}
    </Button>
  );
  
  // Wrap with tooltip if tooltip text is provided
  if (tooltipText) {
    return (
      <Tooltip label={tooltipText} placement="right" hasArrow>
        {button}
      </Tooltip>
    );
  }
  
  return button;
};

export const MapControls = ({ 
  mapTypeId, 
  onMapTypeChange,
  showLabels,
  onToggleLabels,
  isFullscreen,
  onToggleFullscreen
}) => {
  // Define icons with emoji Unicode
  const icons = {
    mapType: '&#127758;&#65039;', // Globe with variation selector
    labels: '&#128207;',          // Map pin with text
    fullscreen: isFullscreen ? '&#128469;' : '&#128470;&#65039;' // Expand/contract icon
  };
  
  // Get current and next map type info
  const getMapTypeInfo = () => {
    switch(mapTypeId) {
      case 'terrain':
        return { 
          current: 'Terrain', 
          next: 'Hybrid',
          color: '#D6AD60' // Gold accent
        };
      case 'hybrid':
        return { 
          current: 'Hybrid', 
          next: 'Satellite',
          color: '#AD8E4B' // Darker gold
        };
      case 'satellite':
        return { 
          current: 'Satellite', 
          next: 'Terrain',
          color: '#9C826B' // Brown accent
        };
      default:
        return { 
          current: 'Map', 
          next: 'Terrain',
          color: '#D6AD60' 
        };
    }
  };
  
  const mapTypeInfo = getMapTypeInfo();
  
  return (
    <Box 
      position="absolute" 
      top={4} 
      left={4} // Changed from right to left
      zIndex={5}
      display="flex"
      flexDirection="column"
      alignItems="flex-start" // Changed from flex-end to flex-start
    >
      <MapControl 
        icon={icons.mapType}
        label={`Map: ${mapTypeInfo.current}`}
        onClick={onMapTypeChange}
        isActive={mapTypeId !== 'terrain'}
        borderColor={mapTypeInfo.color}
        tooltipText={`Change to ${mapTypeInfo.next} view`}
      />
      
      <MapControl
        icon={icons.labels} 
        label={showLabels ? 'Hide Names' : 'Show Names'}
        onClick={onToggleLabels}
        isActive={showLabels}
        borderColor="#9C826B"
        tooltipText={showLabels ? 'Hide site labels' : 'Show site labels'}
      />
      
      <MapControl 
        icon={icons.fullscreen}
        label={isFullscreen ? 'Minimize' : 'Fullscreen'} // Changed text as requested
        onClick={onToggleFullscreen}
        isActive={isFullscreen}
        borderColor="#5E7F5E"
        tooltipText={isFullscreen ? 'Exit fullscreen' : 'View fullscreen map'}
      />
    </Box>
  );
};
